import { cn } from "@u/cn";
import InfoTooltip from "@ui/info-tooltip";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SearchSelect } from "./SearchSelect";

/**
 * Segmented language picker — a row of language-code chips with one active.
 * Used to switch the language being edited on translatable fields (tour/room/
 * floor names, promo title, profile headline) and to pick a primary language.
 *
 * `fill` makes the chips share the row equally (full-width, e.g. above an input);
 * omit it for the compact inline variant (e.g. in a section header). A single
 * language renders as a static chip — there's nothing to switch.
 */
export function LanguageTabs({
  languages,
  active,
  onChange,
  baseLang,
  fill = false,
  className,
  missing,
  labelFor,
}: {
  languages: string[];
  active: string;
  onChange: (lang: string) => void;
  /** Marks this language with a ★ (the base / primary language). */
  baseLang?: string;
  /** Chips share the row equally (full-width). Default: compact/auto-width. */
  fill?: boolean;
  className?: string;
  /** Languages with no value yet — each gets an amber dot (missing translation). */
  missing?: string[];
  /** Display name for a code (e.g. custom languages). Default = i18n lang_<code>. */
  labelFor?: (code: string) => string;
}) {
  const { t } = useTranslation();
  const missingSet = new Set(missing);
  const langName = labelFor ?? ((c: string) => t(`lang_${c}`));
  const missingTitle = t(
    "lang_missing_value",
    "Falta el nombre en este idioma",
  );
  const missingSummary =
    missing && missing.length > 0
      ? t("name_untranslated_warning", {
          langs: missing.map(langName).join(", "),
        })
      : undefined;
  // Reuse InfoTooltip: hover on desktop, tap → drawer on mobile.
  const warnIcon = missingSummary && (
    <InfoTooltip
      icon={<AlertTriangle className="size-3.5 shrink-0 text-warning" />}
      text={missingSummary}
    />
  );

  // Past this many chips the scrolling pill row is unusable (extras hide
  // behind the hidden scrollbar with no affordance) — switch to a native
  // <select> which the platform scrolls for free.
  const MAX_TABS = 6;

  if (languages.length <= 1) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs uppercase text-muted-foreground",
          className,
        )}
        title={
          languages[0] && missingSet.has(languages[0])
            ? missingTitle
            : undefined
        }
      >
        {languages[0] ?? ""}
        {languages[0] && missingSet.has(languages[0]) && (
          <span
            className="size-1.5 rounded-full bg-warning"
            aria-label={missingTitle}
          />
        )}
      </span>
    );
  }

  if (languages.length > MAX_TABS) {
    return (
      <div
        className={cn("flex min-w-0 max-w-full items-center gap-1.5", className)}
      >
        {warnIcon}
        <SearchSelect
          value={active}
          onChange={onChange}
          sort={false}
          className="h-8 w-44 uppercase"
          options={languages.map((code) => ({
            value: code,
            label:
              (code === baseLang ? `${code} ★` : code) +
              (missingSet.has(code) ? " •" : "") +
              ` — ${langName(code)}`,
            keywords: [langName(code)],
          }))}
        />
      </div>
    );
  }

  return (
    <div
      className={cn("flex min-w-0 max-w-full items-center gap-1.5", className)}
    >
      {warnIcon}
      {/* Scrolls horizontally instead of overflowing when there are many
          languages (app + custom). Scrollbar hidden for a clean pill row. */}
      <div className="flex min-w-0 overflow-x-auto rounded-lg border border-border text-xs scrollbar-none [&::-webkit-scrollbar]:hidden">
      {languages.map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => onChange(code)}
          title={missingSet.has(code) ? missingTitle : undefined}
          className={cn(
            "inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap uppercase transition",
            fill ? "flex-1 py-1" : "px-2.5 py-1",
            active === code
              ? "bg-primary-strong text-primary-foreground"
              : "text-muted-foreground hover:bg-muted",
          )}
        >
          {code === baseLang ? `${code} ★` : code}
          {missingSet.has(code) && (
            <span
              aria-label={missingTitle}
              className={cn(
                "size-1.5 rounded-full",
                active === code ? "bg-background/80" : "bg-warning",
              )}
            />
          )}
        </button>
      ))}
      </div>
    </div>
  );
}
